import {useEffect,useState} from 'react';
import {CalendarClock,Code,FileUp,Gauge,Layers,ShieldCheck} from 'lucide-react';
import {api,type Session} from '../api';
import {Button} from '../components/ui/button';
import {Journey} from '../components/policy/Journey';
import {CashFlows,StopRules} from '../components/policy/StopsAndCash';
import {ImportSheet,type Active} from '../components/policy/ImportSheet';
import type {PolicyView} from '../../../src/domain/policy/describe';
import {Preview} from '../components/policy/Preview';
import {AutonomySwitch} from '../components/policy/AutonomySwitch';
import {KnowledgePanel} from '../components/policy/Knowledge';
import {AutonomyLadder} from '../components/policy/AutonomyLadder';
import {CarolInstructions} from '../components/policy/CarolInstructions';
import {useAgentName,withAgent} from '../agent-name';
import '../components/policy/policy.css';

const message=(e:unknown)=>e instanceof Error?e.message:'This step could not be completed.';
/** `?policy=northwind` opens the other reference policy; the page shows AIprise by default. */
const policyId=()=>new URLSearchParams(window.location.search).get('policy')==='northwind'?'northwind':'aiprise';
const sourceNames:Record<string,string>={default:'Reference policy',imported_pdf:'Imported',edited:'Edited in Alder'};

function Facts({view,customers}:{view:PolicyView;customers:number}){
 const agent=useAgentName();
 const o=view.overview;
 const tierNames=view.journey.tiers.map(t=>t.label);
 return <dl className="pj-facts">
  <div><dt><Layers size={15} aria-hidden/>Applies to</dt>
   <dd>{customers} customer{customers===1?'':'s'}, {o.tiers} tier{o.tiers===1?'':'s'}</dd>
   <dd className="pj-fact-sub">{tierNames.join(', ')}</dd></div>
  <div data-tone={o.everyEmailApproved?'safe':'warn'}><dt><ShieldCheck size={15} aria-hidden/>Approval</dt><dd>{withAgent(o.approval,agent)}</dd>
   <dd className="pj-fact-sub">{withAgent(o.approvalDetail,agent)}</dd></div>
  <div><dt><CalendarClock size={15} aria-hidden/>Sends</dt><dd>{o.sendDays.replace('-','–')}, {o.sendHours.replace('-','–')}</dd>
   <dd className="pj-fact-sub">{o.timezone}. {o.timezoneDetail}</dd></div>
  <div><dt><Gauge size={15} aria-hidden/>At most</dt>
   <dd>{o.perDay} email{o.perDay===1?'':'s'} a day, {o.perWeek} a week</dd>
   <dd className="pj-fact-sub">{view.caps.some(c=>c.endsWith('bundled per customer'))?'Per customer, open invoices bundled in one email'
    :'Per customer'}</dd></div>
 </dl>;
}

/** Policy page: the active policy as a journey; owners import, validate, diff and activate new versions in a side sheet. */
/** `embedded` renders it as a section of Settings: no page landmark of its own, and its title one level down. */
export function PolicyPage({session,embedded=false}:{session:Session;embedded?:boolean}){
 const [active,setActive]=useState<Active|null>(null);const [error,setError]=useState('');
 const [showYaml,setShowYaml]=useState(false);const [importing,setImporting]=useState(false);
 const [tab,setTab]=useState<'policy'|'preview'>('policy');
 useEffect(()=>{void api<Active>(`/app-api/policies/${policyId()}`).then(setActive).catch(e=>setError(message(e)));},[]);
 const owner=session.actor.roles.includes('owner');
 const view=active?.summary;
 // The YAML panel and import sheet live on the policy tab, so these heading controls take the owner there.
 const viewYaml=()=>{setTab('policy');setShowYaml(!showYaml);};
 const openImport=()=>{setTab('policy');setImporting(true);};
 const Root=embedded?'section':'main';const Title=embedded?'h2':'h1';
 return <Root id={embedded?undefined:'main-content'} className={`queue-page pj-page${embedded?' settings-embedded':''}`}>
  <header className="page-heading"><div>
   <span className="eyebrow">HOW ALDER COLLECTS</span><Title>Collection policy<span className="heading-dot">.</span></Title>
   <p>The rules every reminder follows.</p></div>
   {active&&<div className="pj-heading-actions">
    <Button variant="outline" aria-pressed={showYaml} onClick={viewYaml}><Code aria-hidden/>
     {showYaml?'Show the journey':'View YAML'}</Button>
    {owner&&<Button onClick={openImport}><FileUp aria-hidden/>Import policy</Button>}</div>}
  </header>
  {active&&<div className="pj-autonomy"><AutonomySwitch mode={active.emailAutonomy} owner={owner} policyId={policyId()}
   onChanged={next=>setActive(next as Active)}/>
   <AutonomyLadder policyId={policyId()} owner={owner}/>
   <CarolInstructions owner={owner}/></div>}
  {error&&<div className="alert error" role="alert">{error}</div>}
  {!active&&!error&&<div className="pj-loading" aria-busy="true"><span className="sr-only">Loading the policy</span></div>}
  {active&&view&&<>
   <section className="pj-hero" aria-labelledby="pj-policy-name">
    <div className="pj-hero-title"><h2 id="pj-policy-name">{view.name}</h2>
     <p><span className="pj-version">{active.version}</span>
      <span>{sourceNames[view.source.kind]??view.source.kind}{view.source.filename?` from ${view.source.filename}`:''}</span>
      {active.history[0]&&<span>Activated {new Date(
       active.history[0].activatedAt).toLocaleDateString()} by {active.history[0].actor}</span>}
     </p></div>
    <Facts view={view} customers={active.customers}/>
   </section>
   <div className="pj-tabs" role="tablist" aria-label="Policy views">
    <button type="button" role="tab" aria-selected={tab==='policy'} onClick={()=>setTab('policy')}>{embedded?'Rules':'Policy'}</button>
    <button type="button" role="tab" aria-selected={tab==='preview'} onClick={()=>setTab('preview')}>Preview</button>
   </div>
   {tab==='preview'?<Preview policyId={policyId()}/>:<>
   {showYaml?<section className="pj-panel" aria-label="Active policy YAML">
    <pre className="policy-yaml pj-yaml">{active.yaml}</pre></section>
    :<><Journey view={view}/>
     <div className="pj-columns"><StopRules view={view}/><div><CashFlows view={view}/>
      {view.neverSay.length>0&&<section className="pj-panel pj-never" aria-labelledby="pj-never-title">
       <h2 id="pj-never-title">Phrases Alder never writes</h2>
       <ul>{view.neverSay.map(p=><li key={p}>{p}</li>)}</ul></section>}</div></div>
     <KnowledgePanel tiers={view.segments.map(s=>({id:s.id,label:s.label}))}/></>}
   <section className="pj-branding" aria-labelledby="pj-branding-title"><h2 id="pj-branding-title" className="pj-section-title">
    Agent identity</h2>
    <p className="muted">The email signature and how customers see your agent live in <a href="#settings">Settings</a>.</p>
   </section>
   {active.history.length>0&&<details className="pj-history"><summary>Version history ({active.history.length})</summary>
    <ol>{active.history.map(h=><li key={h.version}><strong>{h.version}</strong>
     <span>{new Date(h.activatedAt).toLocaleString()}</span><span>{h.actor}</span><span>{h.source}</span></li>)}</ol></details>}
   {!owner&&<p className="pj-owner-note muted">An owner can import a new version of this policy.</p>}
   {owner&&<ImportSheet open={importing} onClose={()=>setImporting(false)} active={active} onActivated={setActive}/>}
   </>}
  </>}
 </Root>;
}
